import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {SingleSelect} from "../../../common/newSelect";
import type {ColumnDef} from "@tanstack/react-table";
import {AutomationExecution, ExecutionStatus} from "crqlar-react-common/dist/models/automation/automation";
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable,
} from "../../../common/serverTable";
import {DateElement} from "../../../common/uiElements/DateElement";
import {
    EXECUTION_STATUS_LABEL_KEYS,
    ExecutionStatusElement,
} from "../../../common/uiElements/ExecutionStatusElement";
import ExecutionDetailsDialog from "./ExecutionDetailsDialog";
import {
    emailSkipReason,
    formatDuration,
    isTestRunExecution,
    runGuestCell,
    stoppedAtText,
} from "./executionFormat.util";
import {reasonText} from "../preview/previewText";
import {automationsBasePath} from "../utils/automationPaths";
import {useFullscreenSnackbarAnchor} from "../../../layout/DashboardSnackbarProvider";

type ExecutionStatusFilter = ExecutionStatus | "ALL";

const EXECUTIONS_POLL_INTERVAL_MS = 5000;

const executionStatuses = [
    ExecutionStatus.PENDING,
    ExecutionStatus.RUNNING,
    ExecutionStatus.WAITING,
    ExecutionStatus.COMPLETED,
    ExecutionStatus.FAILED,
    ExecutionStatus.CANCELLED,
];

function ExecutionTriggerCell({execution}: {execution: AutomationExecution}) {
    const {t} = useTranslation();

    // Never the raw id (it stays in the run's details).
    const cell = runGuestCell(execution, t);
    if (!cell) return null;

    return (
        <Box sx={{minWidth: 0, py: 0.5}}>
            <Typography variant="body2" noWrap>
                {cell.title}
            </Typography>

            {!!cell.detail && (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {cell.detail}
                </Typography>
            )}
        </Box>
    );
}

// A Continue if that stopped the run: it reads Completed, so this line says where, without the error colour.
function ExecutionStoppedAtLine({execution}: {execution: AutomationExecution}) {
    const {t} = useTranslation();
    const text = stoppedAtText(execution, t);
    if (!text) return null;

    return (
        <Typography variant={'body2'} color={'text.secondary'}>
            {text}
        </Typography>
    );
}

function ExecutionErrorCell({message}: {message?: string}) {
    if (!message) return null;

    return (
        <Tooltip title={message}>
            <Typography variant="body2" color="error" noWrap sx={{minWidth: 0}}>
                {message}
            </Typography>
        </Tooltip>
    );
}

export function AutomationExecutionsPage() {
    useTrackScreenVisit('marketing automation runs');
    useFullscreenSnackbarAnchor();

    const {t} = useTranslation();
    const {appId, flowId, executionId} = useParams<{appId: string; flowId?: string; executionId?: string}>();
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const basePath = automationsBasePath(pathname);

    const [selectedStatus, setSelectedStatus] = useState<ExecutionStatusFilter>("ALL");
    const [isPageVisible, setIsPageVisible] = useState(() => document.visibilityState === 'visible');

    useEffect(() => {
        const handleVisibilityChange = () => setIsPageVisible(document.visibilityState === 'visible');

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const listPath = flowId
        ? `${basePath}/${appId}/${flowId}/runs`
        : `${basePath}/${appId}`;
    const executionPath = (id: string) => flowId
        ? `${basePath}/${appId}/${flowId}/runs/${id}`
        : `${basePath}/${appId}/runs/${id}`;

    const flowQuery = useQuery(
        ['automation', flowId],
        () => AutomationService.getFlow(flowId!),
        {enabled: !!flowId, refetchOnWindowFocus: false}
    );
    const flow = flowQuery.data;

    const columns = useMemo<ColumnDef<AutomationExecution>[]>(() => {
        const flowColumns: ColumnDef<AutomationExecution>[] = flowId ? [] : [{
            id: "flowName",
            header: t("Automation"),
            accessorFn: execution => execution.flowName,
            cell: ({row}) => (
                <Typography variant="subtitle2" noWrap>
                    {row.original.flowName || row.original.flowId}
                </Typography>
            ),
            size: 240,
            meta: {defaultActive: true},
        }];

        return [
            ...flowColumns,
            {
                id: "triggerEntity",
                header: t("Guest"),
                accessorFn: execution => execution.triggerEntityType,
                cell: ({row}) => <ExecutionTriggerCell execution={row.original}/>,
                size: 220,
                meta: {defaultActive: true},
            },
            {
                id: "status",
                header: t("Status"),
                accessorFn: execution => execution.status,
                cell: ({row}) => (
                    <Stack gap={0.25} alignItems={'flex-start'}>
                        <ExecutionStatusElement status={row.original.status}/>

                        {isTestRunExecution(row.original) && (
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {t('Test run')}
                            </Typography>
                        )}

                        {/* Completed, but the guest got nothing: said in the preview's words. */}
                        {!!emailSkipReason(row.original) && (
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {t('Email skipped')} · {reasonText(emailSkipReason(row.original), t)}
                            </Typography>
                        )}

                        <ExecutionStoppedAtLine execution={row.original}/>
                    </Stack>
                ),
                size: 170,
                meta: {defaultActive: true, isServerSortable: true},
            },
            {
                id: "startedAt",
                header: t("Started at"),
                accessorFn: execution => execution.startedAt,
                cell: ({row}) => <DateElement date={row.original.startedAt}/>,
                size: 190,
                meta: {defaultActive: true, isServerSortable: true, serverSortField: "insertDate"},
            },
            {
                id: "completedAt",
                header: t("Completed at"),
                accessorFn: execution => execution.completedAt,
                cell: ({row}) => <DateElement date={row.original.completedAt}/>,
                size: 190,
                // Started at + Duration already say it; the column is one click away.
                meta: {defaultActive: false, isServerSortable: true},
            },
            {
                id: "duration",
                header: t("Duration"),
                accessorFn: execution => formatDuration(execution.startedAt, execution.completedAt),
                cell: ({row}) => (
                    <Box>{formatDuration(row.original.startedAt, row.original.completedAt)}</Box>
                ),
                size: 130,
                meta: {defaultActive: true},
            },
            {
                id: "errorMessage",
                header: t("Problem"),
                accessorFn: execution => execution.errorMessage,
                cell: ({row}) => <ExecutionErrorCell message={row.original.errorMessage}/>,
                size: 280,
                meta: {defaultActive: true, grow: true},
            },
            {
                id: "id",
                header: t("ID"),
                accessorFn: execution => execution.id,
                cell: ({row}) => <Box>{row.original.id ?? null}</Box>,
                size: 260,
                meta: {defaultActive: false},
            },
        ];
    }, [flowId, t]);

    const controller = useServerTable<AutomationExecution>({
        tableId: "automation-runs",
        columns,
        getRowId: execution => execution.id!,
        onRowClick: execution => {
            if (execution.id) navigate(executionPath(execution.id));
        },
        initialSorting: [{id: "startedAt", desc: true}],
        defaultPageSize: 10,
        refetchInterval: isPageVisible ? EXECUTIONS_POLL_INTERVAL_MS : false,
        queryDependencies: [flowId, selectedStatus],
        queryFn: async ({pagination, sorting}) => {
            const page = await AutomationService.getExecutions({
                flowId,
                status: selectedStatus === "ALL" ? undefined : selectedStatus,
                page: pagination.pageIndex,
                pageSize: pagination.pageSize,
                sortBy: sorting?.field,
                sortDir: sorting?.direction,
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0,
            };
        },
    });

    const selectedExecution = controller.query.data?.content
        ?.find(execution => execution.id === executionId);

    const handleCloseDialog = () => navigate(listPath, {replace: true});

    return (
        <Box sx={{height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default'}}>
            <Stack
                direction={'row'}
                alignItems={'center'}
                gap={1}
                sx={{
                    px: 2,
                    height: 56,
                    flexShrink: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <IconButton
                    aria-label={t('Back')}
                    onClick={() => navigate(
                        flowId
                            ? `${basePath}/${appId}/${flowId}`
                            : `${basePath}/${appId}`
                    )}
                >
                    <ArrowBackIcon/>
                </IconButton>

                <Typography variant={'h6'} noWrap>
                    {t('Runs')}
                </Typography>

                {!!flow?.name && (
                    <Typography variant={'body1'} color={'text.secondary'} noWrap sx={{minWidth: 0}}>
                        {flow.name}
                    </Typography>
                )}

                {/* The page's one filter sits in its header, beside the name, not in the table toolbar. */}
                <Box sx={{width: 170, flexShrink: 0, ml: 'auto'}}>
                    <SingleSelect
                        value={selectedStatus}
                        options={[
                            {value: "ALL", label: t('All statuses')},
                            ...executionStatuses.map(status => ({
                                value: status,
                                label: t(EXECUTION_STATUS_LABEL_KEYS[status])
                            }))
                        ]}
                        onChange={value => setSelectedStatus(value as ExecutionStatusFilter)}
                        disableClearable
                    />
                </Box>
            </Stack>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    minHeight: 0,
                    px: {xs: 1.5, md: 3},
                    py: 2,
                    boxSizing: 'border-box',
                }}
            >
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <ServerTableToolbar>
                            <ServerTableSort/>
                            <ServerTableFullscreen/>

                            <ServerTableColumns/>
                        </ServerTableToolbar>

                        <ServerTable/>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            {!!executionId && (
                <ExecutionDetailsDialog
                    open
                    executionId={executionId}
                    execution={selectedExecution}
                    flowName={flow?.name}
                    onClose={handleCloseDialog}
                    onOpenExecution={id => navigate(executionPath(id), {replace: true})}
                />
            )}
        </Box>
    );
}

export default AutomationExecutionsPage;
